import { Menu, MenuItem } from "@szhsin/react-menu";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { BasicUser } from "types/topicflow";

import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import InputDatePicker from "@components/input/input-datepicker";
import OrganizationLocationDropdown from "@components/location-dropdown/organization-location-dropdown";
import RecipientDropdown from "@components/people-dropdown/recipient-dropdown";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import {
  TopicflowIconMap,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon-helpers";
import UserCombobox from "@components/user-combobox/user-combobox";
import { classNames } from "@helpers/css";

import { TeamPickerDropdown, TeamPickerTeam } from "../team-picker";
import { hasFilterType, isFilterEqual, updateFilters } from "./helpers";
import {
  EngagementSurveyReportFilterItem,
  EngagementSurveyReportFilterType,
} from "./types";

const filterTypeToTitle = (
  label: UseLabelType,
): { [key in EngagementSurveyReportFilterType]: string } => ({
  [EngagementSurveyReportFilterType.TEAM]: label("team", {
    capitalize: true,
    pluralize: true,
  }),
  [EngagementSurveyReportFilterType.REPORTS_TO]: "Reports To (direct reports)",
  [EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE]:
    "Reports To (full reporting tree)",
  [EngagementSurveyReportFilterType.EMPLOYEE_START_DATE]: "Employee Start Date",
  [EngagementSurveyReportFilterType.LOCATION]: "Location",
});

const Filter = ({
  filter,
  onChange,
  onRemove,
}: {
  filter: EngagementSurveyReportFilterItem;
  onChange: (
    filterItems: TeamPickerTeam[] | (string | null)[] | BasicUser[] | string[],
  ) => void;
  onRemove: () => void;
}) => {
  const label = useLabel();

  const addButton = useMemo(() => {
    switch (filter.type) {
      case EngagementSurveyReportFilterType.TEAM:
        return (
          <TeamPickerDropdown
            teams={filter.data}
            onChange={(team) => onChange([...filter.data, team])}
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </TeamPickerDropdown>
        );
      case EngagementSurveyReportFilterType.REPORTS_TO:
        return (
          <RecipientDropdown
            showCurrentUser
            recipients={filter.data}
            onChangeRecipient={(user) => onChange([...filter.data, user])}
          >
            <UserCombobox.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </RecipientDropdown>
        );
      case EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE:
        return (
          <RecipientDropdown
            showCurrentUser
            recipients={filter.data}
            onChangeRecipient={(user) => onChange([...filter.data, user])}
          >
            <UserCombobox.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </RecipientDropdown>
        );
      case EngagementSurveyReportFilterType.LOCATION:
        return (
          <OrganizationLocationDropdown
            excludeLocations={filter.data}
            onChangeLocation={(location: string) =>
              onChange([...filter.data, location])
            }
          >
            <ComboboxGeneric.Button
              icon={getTopicflowIcon("plus")}
              theme={buttonTheme.iconGray}
            />
          </OrganizationLocationDropdown>
        );
      default:
        return null;
    }
  }, [filter, onChange]);

  const handleRemoveItem = useCallback(
    (id: number | string) => {
      switch (filter.type) {
        case EngagementSurveyReportFilterType.TEAM:
          onChange(filter.data.filter((team) => team.id !== id));
          return;
        case EngagementSurveyReportFilterType.REPORTS_TO:
          onChange(filter.data.filter((manager) => manager.id !== id));
          return;
        case EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE:
          onChange(filter.data.filter((manager) => manager.id !== id));
          return;
        case EngagementSurveyReportFilterType.LOCATION:
          onChange(filter.data.filter((location) => location !== id));
          return;
      }
    },
    [filter, onChange],
  );

  const dataItemsContent = useMemo(() => {
    let dataItems: { label: string; id: number | string }[] = [];
    switch (filter.type) {
      case EngagementSurveyReportFilterType.TEAM:
        dataItems = filter.data.map((team) => ({
          label: team.title,
          id: team.id,
        }));
        break;
      case EngagementSurveyReportFilterType.REPORTS_TO:
        dataItems = filter.data.map((manager) => ({
          label: manager.name,
          id: manager.id,
        }));
        break;
      case EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE:
        dataItems = filter.data.map((manager) => ({
          label: manager.name,
          id: manager.id,
        }));
        break;
      case EngagementSurveyReportFilterType.LOCATION:
        dataItems = filter.data.map((location) => ({
          label: location,
          id: location,
        }));
        break;
    }

    if (filter.type === EngagementSurveyReportFilterType.EMPLOYEE_START_DATE) {
      return (
        <div className="mt-2 flex gap-2 items-center">
          <InputDatePicker
            ariaLabelledBy="Employee start date beginning"
            showIcon
            isClearable
            wrapperClassName="flex-1"
            placeholderText="From"
            date={filter.data[0] ? moment(filter.data[0]).toDate() : null}
            onChangeDate={(date) =>
              onChange([
                date !== null ? moment(date).format("YYYY-MM-DD") : null,
                filter.data[1],
              ])
            }
            dateFormat="MMM d, yyyy"
            className="px-4 py-2 block w-full sm:text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          <TopicflowIcon type="rightArrow" className="text-gray-500 w-4 h-4" />
          <InputDatePicker
            aria-label="Empl"
            ariaLabelledBy="Employee start date end"
            showIcon
            isClearable
            wrapperClassName="flex-1"
            placeholderText="To"
            date={filter.data[1] ? moment(filter.data[1]).toDate() : null}
            onChangeDate={(date) =>
              onChange([
                filter.data[0],
                date !== null ? moment(date).format("YYYY-MM-DD") : null,
              ])
            }
            dateFormat="MMM d, yyyy"
            className="px-4 py-2 block w-full sm:text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      );
    }

    return dataItems.length === 0 ? (
      <div className="mt-2 text-gray-400">No items added</div>
    ) : (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {dataItems.map((item) => (
          <div
            className="border rounded-lg px-2 py-1 text-sm flex items-center gap-1"
            key={item.label}
          >
            {item.label}
            <button
              className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
              onClick={() => handleRemoveItem(item.id)}
            >
              <TopicflowIcon type="close" className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  }, [filter, handleRemoveItem, onChange]);

  return (
    <div className="p-4 border rounded-md">
      <div className="flex items-center justify-between">
        <div className="font-medium">
          {filterTypeToTitle(label)[filter.type]}
        </div>
        <div className="flex items-center gap-2">
          {addButton}
          <Button
            className="text-gray-600"
            icon={TopicflowIconMap.trash}
            theme={buttonTheme.iconGray}
            onClick={onRemove}
          />
        </div>
      </div>
      {dataItemsContent}
    </div>
  );
};

type FilterSelectOption = {
  label: string;
  type: EngagementSurveyReportFilterType;
};

const FilterMenuItem = ({
  option,
  filters,
  onUpdateFilters,
}: {
  option: FilterSelectOption;
  filters: EngagementSurveyReportFilterItem[];
  onUpdateFilters: (filterData: EngagementSurveyReportFilterItem[]) => void;
}) => {
  return (
    <MenuItem
      className="text-sm block"
      onClick={() => {
        const newFilter: EngagementSurveyReportFilterItem = {
          type: option.type,
          data: [],
        };
        if (
          option.type === EngagementSurveyReportFilterType.EMPLOYEE_START_DATE
        ) {
          newFilter.data = [null, null];
        }
        onUpdateFilters([...filters, newFilter]);
      }}
    >
      {option.label}
    </MenuItem>
  );
};

const EngagementSurveyReportFilterContainer = ({
  filters,
  onUpdateFilters,
}: {
  filters: EngagementSurveyReportFilterItem[];
  onUpdateFilters: (filterData: EngagementSurveyReportFilterItem[]) => void;
}) => {
  const label = useLabel();

  const addFilterOptions: FilterSelectOption[] = useMemo(
    () =>
      compact([
        !hasFilterType(
          filters,
          EngagementSurveyReportFilterType.REPORTS_TO,
        ) && {
          label:
            filterTypeToTitle(label)[
              EngagementSurveyReportFilterType.REPORTS_TO
            ],
          type: EngagementSurveyReportFilterType.REPORTS_TO,
        },
        !hasFilterType(
          filters,
          EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE,
        ) && {
          label:
            filterTypeToTitle(label)[
              EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE
            ],
          type: EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE,
        },
        !hasFilterType(filters, EngagementSurveyReportFilterType.TEAM) && {
          label:
            filterTypeToTitle(label)[EngagementSurveyReportFilterType.TEAM],
          type: EngagementSurveyReportFilterType.TEAM,
        },
        !hasFilterType(
          filters,
          EngagementSurveyReportFilterType.EMPLOYEE_START_DATE,
        ) && {
          label:
            filterTypeToTitle(label)[
              EngagementSurveyReportFilterType.EMPLOYEE_START_DATE
            ],
          type: EngagementSurveyReportFilterType.EMPLOYEE_START_DATE,
        },
        !hasFilterType(filters, EngagementSurveyReportFilterType.LOCATION) && {
          label:
            filterTypeToTitle(label)[EngagementSurveyReportFilterType.LOCATION],
          type: EngagementSurveyReportFilterType.LOCATION,
        },
      ]),
    [filters, label],
  );

  const disabled = addFilterOptions.length === 0;
  return (
    <div className="flex flex-col gap-4">
      {filters.map((item, index) => {
        return (
          <Filter
            key={`${item.type}-${index}`}
            filter={item}
            onChange={(data) =>
              onUpdateFilters(updateFilters(filters, item.type, data))
            }
            onRemove={() =>
              onUpdateFilters(filters.filter((f) => !isFilterEqual(f, item)))
            }
          />
        );
      })}

      <Menu
        className="text-sm z-dropdown"
        portal
        transition={false}
        menuButton={
          <button
            disabled={disabled}
            className={classNames(
              "w-fit -ml-2 px-2 py-1 text-left text-sm font-medium rounded flex items-center gap-1.5 text-gray-600 hover:bg-gray-100",
              disabled && "opacity-50",
            )}
          >
            <TopicflowIcon type="plus" className="h-4 w-4" />
            Add Filter
          </button>
        }
      >
        {addFilterOptions.map((option) => {
          return (
            <FilterMenuItem
              key={option.label}
              option={option}
              filters={filters}
              onUpdateFilters={onUpdateFilters}
            />
          );
        })}
      </Menu>
    </div>
  );
};

export default EngagementSurveyReportFilterContainer;
